import { ChangeDetectionStrategy, Component } from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  SiteSelectorConfigurationKeys,
  SiteSelectorConfigurations,
  SITE_SELECTOR_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/site-selector-rules';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import {
  clearFilters,
  resetFilters,
  selectResetPresetOnLogoClickEnabled,
  setSubsites,
} from '@epicuro-next/state/p-basket-state';
import { selectSubSiteId } from '@epicuro-next/state/session-state';
import {
  selectCurrentSite,
  selectSitesWithSubSites,
  setSelectedSite,
} from '@epicuro-next/state/site-state';
import { isNonNullish, sortSites } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Site } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';

@Component({
  selector: 'epc-brand-indicator',
  templateUrl: './brand-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BrandIndicatorComponent {
  public currentSite$ = this.store.pipe(
    select(selectCurrentSite),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public sitesSortingOrder$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        SiteSelectorConfigurationKeys.SitesOrder,
        SiteSelectorConfigurations
      >(SITE_SELECTOR_NAMESPACE, SiteSelectorConfigurationKeys.SitesOrder, []),
    ),
    filter(isNonNullish),
  );

  public sitesWithSubSites$ = this.store.pipe(select(selectSitesWithSubSites));

  public sites$ = combineLatest([
    this.sitesSortingOrder$,
    this.sitesWithSubSites$,
  ]).pipe(map(([sortingOrder, sites]) => sortSites(sortingOrder, sites)));

  public availableSites$ = combineLatest([this.currentSite$, this.sites$]).pipe(
    map(([currentSite, sites]) =>
      sites.filter(
        (site) =>
          !!site.subSites?.length || site.siteId !== currentSite?.siteId,
      ),
    ),
    tap((sites) => this.currentSites$.next(sites)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public multiSiteSupport$ = this.environmentService.environmentSettings.pipe(
    map((settings) => !!JSON.parse(settings.multiSiteSupport)),
  );

  public isMobile$ = atxIsMobile$;

  private currentSites$ = new BehaviorSubject<Site[] | null>(null);

  constructor(
    private store: Store,
    private environmentService: EnvironmentService,
  ) {}

  public onSelectSite(site: Site) {
    const sites = this.currentSites$.value;

    if (sites && sites.length) {
      const params =
        sites.length === 1 && sites[0]?.subSites?.length === 1
          ? { site, skipLocationChange: true }
          : { site };

      this.store.dispatch(setSelectedSite(params));
    }
  }

  public logoClick() {
    combineLatest([
      this.store.pipe(
        select(selectResetPresetOnLogoClickEnabled),
        map(Boolean),
      ),
      this.store.pipe(select(selectSubSiteId)),
    ])
      .pipe(take(1))
      .subscribe(([resetPreset, subSiteId]: [boolean, string | null]) => {
        if (resetPreset) {
          this.store.dispatch(clearFilters({ skipParams: true }));
        } else {
          this.store.dispatch(resetFilters());
        }

        if (subSiteId) {
          this.store.dispatch(setSubsites({ subsites: subSiteId }));
        }
      });
  }
}
